/**
 * Precision CAP Portal — MFA challenge.
 *
 * Used at every sign-in after the first one. The login response gave us a
 * short-lived challenge_token; we trade it for a session by submitting the
 * current 6-digit code from the user's authenticator app.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';

const API = process.env.REACT_APP_API_URL || '';

export default function PortalMfaChallenge() {
  const location = useLocation();
  const navigate = useNavigate();
  const codeRef = useRef(null);

  const challengeToken =
    location.state?.challenge_token ||
    sessionStorage.getItem('portal_challenge_token') ||
    '';

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!challengeToken) {
      navigate('/login');
      return;
    }
    if (codeRef.current) codeRef.current.focus();
  }, [challengeToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const trimmed = code.trim().replace(/\s+/g, '');
      const res = await fetch(`${API}/api/portal/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          challenge_token: challengeToken,
          code: trimmed,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(
          data.message ||
            'That code did not match. Make sure your authenticator is showing the current code, then try again.',
        );
        setCode('');
        setLoading(false);
        return;
      }
      sessionStorage.removeItem('portal_challenge_token');
      navigate('/cap');
    } catch {
      setError('We could not reach the server. Please try again.');
      setLoading(false);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-md mx-auto">
        <h1 className="text-[28px] font-semibold text-slate-900 leading-tight">
          Enter your 6-digit code
        </h1>
        <p className="text-slate-600 mt-2 text-[15px]">
          Open your authenticator app and enter the code currently shown for Precision CAP
          Portal.
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-8 bg-white ring-1 ring-slate-200 rounded-xl p-6 space-y-5"
        >
          <div>
            <label
              htmlFor="portal-challenge-code"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              6-digit code
            </label>
            <input
              ref={codeRef}
              id="portal-challenge-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              maxLength={6}
              className="w-full px-3 py-2.5 text-lg tracking-widest text-center font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              required
            />
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !code.trim()}
            className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white transition ${
              loading || !code.trim()
                ? 'bg-slate-400 cursor-not-allowed'
                : 'bg-slate-900 hover:bg-slate-800'
            }`}
          >
            {loading ? 'Verifying…' : 'Verify and continue'}
          </button>

          <p className="text-[12px] text-slate-500">
            The code rotates every 30 seconds. If it changes while you're typing, just enter
            the new one.
          </p>
        </form>
      </div>
    </PortalLayout>
  );
}
